import { MANIFEST_FIELDS } from '@/graphql/queries/manifests';
import { INBOUND_ITEM_FIELDS, INBOUND_ITEM_SUMMARY_FIELDS } from '@/graphql/queries/manifests_inbound_items';
import { ROUTE_MANIFEST_FIELDS, ROUTE_MANIFEST_SUMMARY_FIELDS } from '@/graphql/queries/routes';
import { EXCEPTION_FIELDS } from '@/graphql/queries/db_exceptions';
import { MANIFEST_SUMMARY_VIEW_FIELDS } from '@/graphql/fragments/manifest_summary_view';
import { gql } from '@apollo/client';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { PALLET_ITEM_FIELDS } from '@/graphql/queries/pallet_items';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';

export const GET_MANIFESTS = gql`
    ${MANIFEST_FIELDS}
    ${INBOUND_ITEM_FIELDS}
    ${ROUTE_MANIFEST_FIELDS}
    query GetManifests($client_id: uuid!, $cutoff: timestamptz!, $filters: [manifests_bool_exp!] = []) {
        manifests(
            where: {
                client_id: { _eq: $client_id }
                created_at: { _gte: $cutoff }
                _or: [{ route_id: { _is_null: true } }, { route: { status: { _neq: "deleted" } } }]
                _and: $filters
            }
            order_by: { created_at: desc }
        ) {
            ...ManifestFields
            items {
                ...InboundItemFields
            }
            route {
                ...RouteManifestFields
            }
        }
    }
`;

export const GET_MANIFESTS_SUMMARY = gql`
    ${MANIFEST_FIELDS}
    ${INBOUND_ITEM_SUMMARY_FIELDS}
    ${ROUTE_MANIFEST_SUMMARY_FIELDS}
    ${MANIFEST_SUMMARY_VIEW_FIELDS}
    query GetManifestsSummary($client_id: uuid!, $cutoff: timestamptz!, $filters: [manifests_bool_exp!] = []) {
        manifests(
            where: {
                client_id: { _eq: $client_id }
                created_at: { _gte: $cutoff }
                _or: [{ route_id: { _is_null: true } }, { route: { status: { _neq: "deleted" } } }]
                _and: $filters
            }
            order_by: { created_at: desc }
        ) {
            ...ManifestFields
            # Picker / Staging Verifier columns (Outbound tab). Kept out of the shared fragments.
            assigned_teammate_id
            assigned_teammate {
                teammate_id
                username
            }
            items {
                ...InboundItemSummaryFields
            }
            route {
                ...RouteManifestSummaryFields
                needs_verification
                staging_verification {
                    staging_verification_id
                    status
                    assigned_teammate_id
                    assigned_teammate {
                        teammate_id
                        username
                    }
                }
            }
            warehouse_summary {
                ...ManifestSummaryViewFields
            }
        }
    }
`;

export const GET_EXTERNAL_MANIFESTS_SUMMARY = gql`
    ${MANIFEST_FIELDS}
    ${INBOUND_ITEM_SUMMARY_FIELDS}
    ${ROUTE_MANIFEST_SUMMARY_FIELDS}
    ${MANIFEST_SUMMARY_VIEW_FIELDS}
    query GetExternalManifestsSummary($client_id: uuid!, $cutoff: timestamptz!, $filters: [manifests_bool_exp!] = []) {
        manifests(
            where: {
                items: { item: { order: { shipper_id: { _eq: $client_id } } } }
                client_id: { _neq: $client_id }
                share_manifest: { _eq: true }
                created_at: { _gte: $cutoff }
                _or: [{ route_id: { _is_null: true } }, { route: { status: { _neq: "deleted" } } }]
                _and: $filters
            }
            order_by: { created_at: desc }
        ) {
            ...ManifestFields
            items {
                ...InboundItemSummaryFields
            }
            route {
                ...RouteManifestSummaryFields
            }
            warehouse_summary {
                ...ManifestSummaryViewFields
            }
        }
    }
`;

// Returning only ManifestFields: callers use just manifest_id/manifest_number from the result
// and refetch elsewhere for anything else. Returning the full item→order→sibling-items graph here
// duplicates it once per inserted item, which blows Postgres's string buffer past 1GB on manifests
// with 1000+ items.
export const INSERT_MANIFEST = gql`
    ${MANIFEST_FIELDS}
    mutation InsertManifest($manifest: manifests_insert_input!) {
        created: insert_manifests_one(object: $manifest) {
            ...ManifestFields
        }
    }
`;

export const GET_ORDERS = gql`
    ${EXCEPTION_FIELDS}
    ${ITEM_FIELDS}
    ${MANIFEST_FIELDS}
    ${PALLET_ITEM_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}

    query GetManifestOrders($client_id: uuid!, $cutoff: timestamptz, $filters: [orders_bool_exp!] = []) {
        orders(
            where: {
                _or: [
                    { carrier_id: { _eq: $client_id } }
                    { warehouse_carrier_id: { _eq: $client_id } }
                    { _and: [{ shipper_id: { _eq: $client_id } }, { carrier_id: { _is_null: true } }] }
                ]
                order_status: { _in: ["pending", "active", "routed", "claimed", "inProgress", "routedInProgress"] }
                _and: $filters
                created_at: { _gte: $cutoff }
            }
            order_by: { created_at: desc }
        ) {
            order_id
            order_number
            order_status
            order_type
            forward_stocked_status
            po_number
            shipper_id
            reference_id
            dropoff_name
            # Order-level fallback for the picker's Est. Received Date column: the wh_event carries
            # the estimate once the order is claimed, but an order can reach the picker before one
            # exists, and the receive path never copies the estimate onto AWAITING:RECEIVED.
            warehouse_estimated_delivery_date
            order_shipper {
                client_id
                business_name
            }
            wh_events(
                order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]
            ) {
                ...WarehouseEvents
                transition {
                    next
                    current
                }
            }
            itemsByOrderId(order_by: { created_at: desc }) {
                ...ItemFields
                manifests {
                    mapping_id
                    manifest_id
                    item_id
                    manifest {
                        ...ManifestFields
                    }
                }
            }
        }
    }
`;

export const UPSERT_ITEMS = gql`
    ${ITEM_FIELDS}
    mutation UpsertItems($items: [items_insert_input!]!) {
        insert_items(objects: $items, on_conflict: { constraint: items_pkey, update_columns: [quantity, pallet_id] }) {
            returning {
                ...ItemFields
                pallet {
                    pallet_id
                    type
                    pallet_number
                    pallet_name
                    is_pool
                    warehouse_id
                    warehouse_location
                    warehouse_status
                }
            }
        }
    }
`;

// insert_manifests returns only ManifestFields — see the comment on INSERT_MANIFEST above for why
// the items/route sub-selections were dropped.
export const INSERT_MANIFEST_AND_UPSERT_ITEMS = gql`
    ${MANIFEST_FIELDS}
    ${ITEM_FIELDS}
    mutation InsertManifestAndUpsertItems($manifests: [manifests_insert_input!]!, $items: [items_insert_input!]!) {
        insert_items(objects: $items, on_conflict: { constraint: items_pkey, update_columns: [quantity, pallet_id] }) {
            returning {
                ...ItemFields
                pallet {
                    pallet_id
                    type
                    pallet_number
                    pallet_name
                    is_pool
                    warehouse_id
                    warehouse_location
                    warehouse_status
                }
            }
        }
        created: insert_manifests(
            objects: $manifests
            on_conflict: {
                constraint: manifests_pkey
                update_columns: [warehouse_id, draft_items, name, receiving_date, share_manifest]
            }
        ) {
            returning {
                ...ManifestFields
            }
        }
    }
`;

export const DELETE_MANIFEST_BY_PK = gql`
    mutation MyMutation($manifest_id: uuid!) {
        delete_manifests_by_pk(manifest_id: $manifest_id) {
            manifest_id
            manifest_number
        }
    }
`;

// Remove item↔manifest mappings when updating an existing manifest's membership.
export const DELETE_MANIFEST_INBOUND_ITEMS = gql`
    mutation DeleteManifestInboundItems($manifest_id: uuid!, $item_ids: [uuid!]!) {
        delete_manifests_inbound_items(where: { manifest_id: { _eq: $manifest_id }, item_id: { _in: $item_ids } }) {
            affected_rows
        }
    }
`;
